import React, { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import {
  BookOpenIcon,
  FileTextIcon,
  SparkleIcon,
  SpinnerGapIcon,
  TrashIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
  XIcon,
} from "@phosphor-icons/react";

import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import { GuidelineProcessingStatus, type InstitutionalGuidelinesBySchoolQuery } from "@axio/graphql/generated/graphql";

export type GuidelineUploadState = "idle" | "uploading" | "uploaded" | "creating";

type GuidelineViewModel = InstitutionalGuidelinesBySchoolQuery["institutionalGuidelinesBySchool"]["data"][number];

interface GuidelinesTabProps {
  guidelines: GuidelineViewModel[];
  loadingGuidelines: boolean;
  canUploadGuidelines: boolean;
  selectedFileName: string | null;
  selectedFileSize: number | null;
  uploadState: GuidelineUploadState;
  uploadError: string | null;
  deletingGuidelineId: string | null;
  onSelectFile: () => void;
  onClearFile: () => void;
  onCreateGuideline: () => Promise<boolean> | boolean;
  onDeleteGuideline: (guidelineId: string) => void;
}

const formatFileSize = (size: number | null) => {
  if (!size) {
    return "";
  }

  if (size >= 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));

function GuidelinesTab({
  guidelines,
  loadingGuidelines,
  canUploadGuidelines,
  selectedFileName,
  selectedFileSize,
  uploadState,
  uploadError,
  deletingGuidelineId,
  onSelectFile,
  onClearFile,
  onCreateGuideline,
  onDeleteGuideline,
}: GuidelinesTabProps) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const isBusy = uploadState === "uploading" || uploadState === "creating";
  const orderedGuidelines = useMemo(
    () => [...guidelines].sort((left, right) => new Date(left.created).getTime() - new Date(right.created).getTime()),
    [guidelines]
  );
  const hasGuidelines = orderedGuidelines.length > 0;

  const handleCreateGuideline = async () => {
    const created = await onCreateGuideline();
    if (created) {
      setUploadOpen(false);
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollContent}>
        <View style={styles.contentInner}>
          <View style={styles.explainer}>
            <CustomText
              text="Institutional guidelines are non-academic policies the AI references when evaluating courses — for example, faith-based perspectives, inclusive language requirements, or institutional values. These are used alongside the evaluation rubric to ensure courses meet your institution's broader standards."
              size="xs"
              style={styles.secondaryText}
              useTranslationText={false}
            />
          </View>

          {!canUploadGuidelines ? (
            <View style={styles.lockedCallout}>
              <WarningCircleIcon color="#B54708" size={18} weight="bold" />
              <View style={styles.calloutText}>
                <CustomText
                  text="Upload and activate a rubric before adding guidelines."
                  size="xs"
                  weight="medium"
                  style={styles.warningText}
                  useTranslationText={false}
                />
              </View>
            </View>
          ) : null}

          {loadingGuidelines ? (
            <View style={styles.processingInline}>
              <SpinnerGapIcon color="#3E68FE" size={18} weight="bold" />
              <CustomText text="Loading guidelines..." size="xs" weight="medium" useTranslationText={false} />
            </View>
          ) : null}

          {!loadingGuidelines && !hasGuidelines ? (
            <View style={styles.emptyState}>
              <BookOpenIcon color="#D0D5DD" size={40} weight="regular" />
              <CustomText text="No guidelines uploaded" size="s" weight="bold" useTranslationText={false} />
              <CustomText
                text="Upload institutional policy documents that the AI should reference during evaluations."
                size="xs"
                style={{ ...styles.secondaryText, textAlign: "center" }}
                useTranslationText={false}
              />
            </View>
          ) : null}

          <View style={styles.guidelineList}>
            {orderedGuidelines.map((guideline) => {
              const isDeleting = deletingGuidelineId === guideline.id;
              const isProcessing = [GuidelineProcessingStatus.Pending, GuidelineProcessingStatus.Processing].includes(
                guideline.processingStatus
              );
              const isFailed = guideline.processingStatus === GuidelineProcessingStatus.Failed;

              if (isProcessing) {
                return (
                  <View key={guideline.id} style={styles.guidelineProcessingRow}>
                    <SpinnerGapIcon color="#3E68FE" size={16} weight="bold" />
                    <View style={styles.processingTextStack}>
                      <CustomText
                        text="Processing guideline..."
                        size="xs"
                        weight="medium"
                        style={styles.brandText}
                        useTranslationText={false}
                      />
                      <CustomText
                        text="Analyzing document and generating summary"
                        size="xs"
                        style={styles.brandText}
                        useTranslationText={false}
                      />
                    </View>
                  </View>
                );
              }

              return (
                <View key={guideline.id} style={isFailed ? styles.failedCard : styles.guidelineCard}>
                  <View style={styles.guidelineHeader}>
                    <View style={styles.fileIconWrap}>
                      <FileTextIcon color="#3E68FE" size={18} weight="regular" />
                    </View>
                    <View style={styles.guidelineCopy}>
                      <CustomText text={guideline.name} size="s" weight="medium" useTranslationText={false} />
                      <CustomText
                        text={`${guideline.sourceFile.fileName} - uploaded ${formatDate(guideline.created)}`}
                        size="xs"
                        style={styles.secondaryText}
                        useTranslationText={false}
                      />
                    </View>
                    <Pressable
                      role="button"
                      accessibilityLabel={`Delete ${guideline.name}`}
                      disabled={isDeleting}
                      onPress={() => onDeleteGuideline(guideline.id)}
                      style={styles.iconButton}>
                      {isDeleting ? (
                        <SpinnerGapIcon color="#667085" size={16} weight="bold" />
                      ) : (
                        <TrashIcon color="#667085" size={16} />
                      )}
                    </Pressable>
                  </View>

                  {isFailed ? (
                    <View style={styles.errorCallout}>
                      <WarningCircleIcon color="#B42318" size={18} weight="bold" />
                      <CustomText
                        text={guideline.processingError || "Guideline processing failed"}
                        size="xs"
                        style={styles.errorText}
                        useTranslationText={false}
                      />
                    </View>
                  ) : null}

                  {guideline.summary ? (
                    <View style={styles.summaryPanel}>
                      <View style={styles.summaryHeader}>
                        <SparkleIcon color="#3E68FE" size={14} weight="fill" />
                        <CustomText text="AI Summary" size="xs" weight="medium" useTranslationText={false} />
                      </View>
                      <CustomText
                        text={guideline.summary}
                        size="xs"
                        style={styles.secondaryText}
                        useTranslationText={false}
                      />
                    </View>
                  ) : null}
                </View>
              );
            })}
          </View>
        </View>
      </ScrollView>

      <View style={styles.footerActions}>
        {uploadOpen && canUploadGuidelines ? (
          <View>
            <View style={styles.uploadDropZone}>
              {selectedFileName ? (
                <View style={styles.selectedFileRow}>
                  <FileTextIcon color="#3E68FE" size={20} weight="regular" />
                  <View style={styles.guidelineCopy}>
                    <CustomText text={selectedFileName} size="xs" weight="medium" useTranslationText={false} />
                    <CustomText
                      text={formatFileSize(selectedFileSize)}
                      size="xs"
                      style={styles.secondaryText}
                      useTranslationText={false}
                    />
                  </View>
                  <Pressable
                    role="button"
                    accessibilityLabel="Remove selected guideline file"
                    onPress={onClearFile}
                    disabled={isBusy}
                    style={styles.iconButton}>
                    <XIcon color="#344054" size={16} />
                  </Pressable>
                </View>
              ) : (
                <View style={styles.inlineUploadPrompt}>
                  <UploadSimpleIcon color="#667085" size={16} weight="regular" />
                  <CustomText
                    text="Drop file here or"
                    size="xs"
                    style={styles.secondaryText}
                    useTranslationText={false}
                  />
                  <Pressable
                    role="button"
                    accessibilityLabel="Browse guideline files"
                    disabled={isBusy}
                    onPress={onSelectFile}>
                    <CustomText
                      text={uploadState === "uploading" ? "uploading..." : "browse"}
                      size="xs"
                      weight="medium"
                      style={styles.browseText}
                      useTranslationText={false}
                    />
                  </Pressable>
                </View>
              )}
            </View>

            {uploadError ? (
              <View style={styles.errorCallout}>
                <WarningCircleIcon color="#B42318" size={18} weight="bold" />
                <CustomText text={uploadError} size="xs" style={styles.errorText} useTranslationText={false} />
              </View>
            ) : null}

            {selectedFileName ? (
              <View style={styles.uploadActions}>
                <CustomButton
                  text="Cancel"
                  styleType="lightStroke"
                  size="s"
                  useTranslationText={false}
                  disabled={isBusy}
                  onPress={() => setUploadOpen(false)}
                />
                <CustomButton
                  text={uploadState === "creating" ? "Processing..." : "Upload Guideline"}
                  styleType="primary"
                  size="s"
                  useTranslationText={false}
                  disabled={isBusy}
                  onPress={handleCreateGuideline}
                />
              </View>
            ) : (
              <Pressable
                role="button"
                accessibilityLabel="Cancel guideline upload"
                disabled={isBusy}
                onPress={() => setUploadOpen(false)}
                style={styles.cancelUploadTextButton}>
                <CustomText text="Cancel" size="xs" style={styles.mutedText} useTranslationText={false} />
              </Pressable>
            )}
          </View>
        ) : (
          <CustomButton
            text="Upload Guideline"
            styleType="lightStroke"
            weight="medium"
            style={styles.footerButton}
            size="s"
            useTranslationText={false}
            disabled={!canUploadGuidelines}
            onPress={() => setUploadOpen(true)}
            leftIcon={<UploadSimpleIcon color="#344054" size={16} weight="bold" />}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    flex: 1,
    minHeight: 0,
  },
  contentInner: {
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  secondaryText: {
    color: "#344054",
  },
  mutedText: {
    color: "#667085",
  },
  browseText: {
    color: "#3E68FE",
  },
  warningText: {
    color: "#B54708",
  },
  errorText: {
    color: "#B42318",
  },
  brandText: {
    color: "#3E68FE",
  },
  explainer: {
    padding: 14,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  lockedCallout: {
    marginTop: 12,
    flexDirection: "row",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FFFAEB",
    borderWidth: 1,
    borderColor: "#FEDF89",
  },
  calloutText: {
    flex: 1,
    gap: 2,
  },
  emptyState: {
    alignItems: "center",
    gap: 8,
    paddingVertical: 36,
    paddingHorizontal: 24,
  },
  guidelineList: {
    gap: 12,
    marginTop: 16,
  },
  guidelineCard: {
    gap: 12,
    padding: 14,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  failedCard: {
    gap: 12,
    padding: 14,
    borderRadius: 8,
    backgroundColor: "#FFFBFA",
    borderWidth: 1,
    borderColor: "#FECDCA",
  },
  guidelineHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  fileIconWrap: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    backgroundColor: "#F5F8FF",
  },
  guidelineCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  iconButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    minHeight: 36,
    padding: 8,
    borderRadius: 8,
  },
  processingInline: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#F5F8FF",
    borderWidth: 1,
    borderColor: "#D1E0FF",
  },
  guidelineProcessingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: "#F5F8FF",
    borderWidth: 1,
    borderColor: "#D1E0FF",
  },
  processingTextStack: {
    gap: 2,
  },
  errorCallout: {
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#FECDCA",
  },
  summaryPanel: {
    gap: 6,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
  },
  summaryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  uploadDropZone: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderStyle: "dashed",
    borderColor: "#D0D5DD",
    borderRadius: 8,
    padding: 16,
    backgroundColor: "#FFFFFF",
  },
  inlineUploadPrompt: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  selectedFileRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    width: "100%",
  },
  cancelUploadTextButton: {
    alignItems: "center",
    paddingVertical: 8,
    marginTop: 8,
  },
  uploadActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 12,
  },
  footerActions: {
    gap: 8,
    flexShrink: 0,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    backgroundColor: "#FFFFFF",
  },
  footerButton: {
    justifyContent: "center",
    color: "#344054",
  },
});

export default GuidelinesTab;
